import React, { useCallback, useRef, useState, useEffect } from 'react';
import update from 'immutability-helper';
import { useDrag, useDrop } from 'react-dnd';
import { DragOutlined } from "@ant-design/icons";
import ResponsiveTable from '@/components/responsiveTable';

interface DraggableBodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
}

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}: DraggableBodyRowProps) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: { index: number }) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

const DraggableTable = (props: any) => {
  const {
    dataSource = [],
    columns = [],
    params = {}, // update sort 所需要用到的參數object
  } = props;
  const [data, setData] = useState(dataSource);
  const {
    updateSortFunction,
    hasUpdatePermission = true,
  } = params;
  const newColumns = hasUpdatePermission
    ? [{ title: 'Sort', dataIndex: 'index', render: () => <DragOutlined /> }, ...columns]
    : [...columns];

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback((dragIndex: number, hoverIndex: number) => {
    if (!hasUpdatePermission) {
      return;
    }

    const dragRow = data[dragIndex];
    const newData = update(data, { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] });

    setData(newData);
    updateSortFunction(newData);
  }, [data, updateSortFunction, hasUpdatePermission]);

  useEffect(() => {
    setData(dataSource);
  }, [dataSource])

  return (
    <ResponsiveTable
      columns={newColumns}
      dataSource={data}
      components={components}
      onRow={(_: any, index: any) => {
        const attr = {
          index,
          moveRow,
        };
        return attr as React.HTMLAttributes<any>;
      }}
    />
  );
};

export default DraggableTable;